import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getAllCompanies,
  getCompanyById,
  getTotalActiveCompanies,
  updateCompany,
  createNewCompany,
  getUnassignedCompaniesToGroup,
  getAssignedCompaniesToGroup,
  getUnassignedCompaniesToUser,
  getAssignedCompaniesToUser,
  listAllCompanies,
  updateManyCompanies,
  findByUser,
  getExistingCompany,
} from '@/api/company/company';
import { ListAllCompany } from '@/api/entities/company';
import { StructureAdvancedFilter } from '@/entities/structure/group';
import { TCompanySchema } from '@/schemas/company/company.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { TotalActiveCompany } from '@/api/entities/company';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';
import { useAuth } from './auth.hooks';

export const useCreateCompany = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TCompanySchema) =>
      createNewCompany(data, {
        createdByUserId: limitedRoleLoggedInUserId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingCompany'] });
      queryClient.invalidateQueries({ queryKey: ['listAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['listAllBranches'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedCompaniesToGroup'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedCompaniesToUser'],
      });

      notify(t('common:companyCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetExistingCompany = (query?: TCompanySchema) => {
  return useQuery({
    queryKey: ['getExistingCompany', query],
    queryFn: () => getExistingCompany(query),
  });
};

export const useListAllCompanies = (query?: ListAllCompany) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['listAllCompanies', query, limitedRoleLoggedInUserId],
    queryFn: () =>
      listAllCompanies({ ...query, userId: limitedRoleLoggedInUserId }),
  });
};

export const useGetAllCompanies = (
  filter: StructureAdvancedFilter,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  return useQuery({
    queryKey: ['getAllCompanies', filter, page, sortBy, sortDirection],
    queryFn: () => getAllCompanies(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveCompanies = (query?: TotalActiveCompany) => {
  return useQuery({
    queryKey: ['getTotalActiveCompanies', query],
    queryFn: () => getTotalActiveCompanies(query),
  });
};

export const useGetCompanyById = (id: string) => {
  return useQuery({
    queryKey: ['getCompanyById', id],
    queryFn: () => getCompanyById(id),
    enabled: !!id,
  });
};

export const useGetUnassignedCompaniesToGroup = () => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['getUnassignedCompaniesToGroup'],
    queryFn: () => getUnassignedCompaniesToGroup(limitedRoleLoggedInUserId),
  });
};

export const useGetAssignedCompaniesToGroup = (groupId: string) => {
  return useQuery({
    queryKey: ['getAssignedCompaniesToGroup', groupId],
    queryFn: () => getAssignedCompaniesToGroup(groupId),
    enabled: !!groupId,
  });
};

export const useGetUnassignedCompaniesToUser = (userId: string) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['getUnassignedCompaniesToUser', userId],
    queryFn: () =>
      getUnassignedCompaniesToUser(userId, limitedRoleLoggedInUserId),
    enabled: !!userId,
  });
};

export const useGetAssignedCompaniesToUser = (userId: string) => {
  return useQuery({
    queryKey: ['getAssignedCompaniesToUser', userId],
    queryFn: () => getAssignedCompaniesToUser(userId),
    enabled: !!userId,
  });
};

export const useUpdateCompany = ({
  t,
  showDefaultSuccessMessage = true,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (company: TCompanySchema) => updateCompany(company),
    onSuccess: (_, variables) => {
      const { id, groupId } = variables;

      queryClient.invalidateQueries({ queryKey: ['getAllGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingCompany'] });
      queryClient.invalidateQueries({ queryKey: ['listAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['getCompanyById', id] });
      queryClient.invalidateQueries({ queryKey: ['listAllBranches'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });

      queryClient.invalidateQueries({
        queryKey:
          groupId !== null
            ? ['getAssignedCompaniesToGroup', groupId]
            : ['getAssignedCompaniesToGroup'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedCompaniesToGroup'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedCompaniesToUser'],
      });

      if (showDefaultSuccessMessage) {
        notify(t('common:companyUpdatedSuccessfully'), NOTIFY.SUCCESS);
      }

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating company:', error);
    },
  });
};

export const useUpdateManyCompanies = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companies: TCompanySchema[]) => updateManyCompanies(companies),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getCompanyById', item?.id],
        });

        queryClient.invalidateQueries({
          queryKey:
            item?.groupId !== null
              ? ['getAssignedCompaniesToGroup', item?.groupId]
              : ['getAssignedCompaniesToGroup'],
        });
      }

      queryClient.invalidateQueries({ queryKey: ['getAllGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingCompany'] });
      queryClient.invalidateQueries({ queryKey: ['listAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['listAllBranches'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedCompaniesToGroup'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedCompaniesToUser'],
      });

      notify(t('common:companyUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating companies:', error);
    },
  });
};

export const useGetCompaniesSelectOptions = (query?: ListAllCompany) => {
  const { data: allCompanies, isLoading: isLoadingAllCompanies } =
    useListAllCompanies(query);

  const getCompaniesSelectOptions = () => {
    if (
      !isLoadingAllCompanies &&
      allCompanies &&
      allCompanies.data.length > 0
    ) {
      return allCompanies.data.map((group: TCompanySchema) => {
        const { id, name } = group;

        return {
          label: name,
          value: id,
        };
      });
    }

    return undefined;
  };

  return { getCompaniesSelectOptions, isLoadingAllCompanies };
};

export const useGetByUser = (userId: string, roleName: string) => {
  return useQuery({
    queryKey: ['getCompaniesByGroup', userId, roleName],
    queryFn: () => findByUser(userId, roleName),
  });
};
